/**
 * Curating the shared section library.
 *
 * Platform staff only, and the reason is not privilege for its own sake: this
 * catalogue is read by every workspace, so somebody's typo is forty customers'
 * typo. Drafts exist so a section can be built without being anybody's problem
 * yet, and publishing is refused for an empty one.
 *
 * Editing an entry changes nothing anybody has already imported — imports copy.
 * What an edit does is bump the version, which is how a workspace holding an
 * older copy comes to be *told*, rather than silently changed.
 */

import { useEffect, useState } from 'react';

import QuestionFields, {
  blankQuestion,
  type QuestionDraft,
  type QuestionType,
} from '../../components/QuestionFields';
import Spinner from '../../components/Spinner';
import {
  Button,
  Chip,
  ConfirmButton,
  Empty,
  Field,
  Input,
  Notice,
  Select,
  Textarea,
} from '../../components/ui';
import { ApiError, api, describeError } from '../../lib/api';
import ConsoleLayout from './ConsoleLayout';

interface LibraryQuestion {
  id: string;
  ordinal: number;
  type: string;
  prompt: string;
  hint: string | null;
  points: number;
  answer: Record<string, unknown>;
  tags: string[];
}

interface Section {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  kind: string;
  durationMs: number;
  allowBack: boolean;
  instructions: string | null;
  language: string;
  tags: string[];
  version: number;
  published: boolean;
  archived: boolean;
  questionCount: number;
  questions?: LibraryQuestion[];
}

/** The section kinds a tenant composition accepts. Anything else stores fine and
 *  fails at the moment a workspace tries to import it. */
const KINDS = ['cognitive', 'situational', 'timed-writing'];

function minutes(ms: number): string {
  return `${Math.round(ms / 60000)} min`;
}

interface EditingQuestion {
  /** null while adding: the row does not exist yet. */
  id: string | null;
  draft: QuestionDraft;
}

/** A stored question in the shape the shared editor works on. */
function toDraft(question: LibraryQuestion): QuestionDraft {
  return {
    type: question.type as QuestionType,
    prompt: question.prompt,
    hint: question.hint,
    points: question.points,
    timeLimitMs: null,
    answer: question.answer ?? {},
    tags: question.tags ?? [],
  };
}

function QuestionEditor({
  state,
  onChange,
  onCancel,
  onSave,
  busy,
  error,
  field,
}: {
  state: EditingQuestion;
  onChange: (next: EditingQuestion) => void;
  onCancel: () => void;
  onSave: () => void;
  busy: boolean;
  error: string | null;
  field: string | null;
}) {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
      className="mt-4 rounded-xl border border-veil/[0.07] bg-black/20 p-4"
    >
      <QuestionFields
        draft={state.draft}
        onChange={(draft) => onChange({ ...state, draft })}
        error={error}
        field={field}
      />
      <div className="mt-5 flex gap-2">
        <Button type="submit" variant="primary" busy={busy}>
          {state.id ? 'Save' : 'Add it'}
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}


function Detail({ section, onChanged }: { section: Section; onChanged: () => void }) {
  const [full, setFull] = useState<Section | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [field, setField] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<EditingQuestion | null>(null);

  useEffect(() => {
    void api
      .get<Section>(`/api/console/library/${section.id}`)
      .then(setFull)
      .catch((failure) => setError(describeError(failure)));
  }, [section.id]);

  const act = async (run: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    setField(null);
    try {
      await run();
      setFull(await api.get<Section>(`/api/console/library/${section.id}`));
      onChanged();
      return true;
    } catch (failure) {
      setError(describeError(failure));
      // Carried separately so a refusal the server could attribute to an input
      // renders beside that input rather than as a banner somebody has to map
      // back themselves.
      setField(failure instanceof ApiError ? failure.field : null);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const questions = full?.questions ?? [];

  const saveQuestion = async () => {
    if (!editing) return;
    const payload = {
      type: editing.draft.type,
      prompt: editing.draft.prompt,
      hint: editing.draft.hint,
      points: editing.draft.points,
      timeLimitMs: editing.draft.timeLimitMs,
      answer: editing.draft.answer,
      tags: editing.draft.tags,
    };
    const ok = await act(() =>
      editing.id
        ? api.patch(`/api/console/library/questions/${editing.id}`, payload)
        : api.post(`/api/console/library/${section.id}/questions`, payload),
    );
    if (ok) setEditing(null);
  };

  /** Order is what a candidate sees, so it is stored rather than inferred. */
  const move = async (index: number, by: number) => {
    const next = [...questions];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    const moved = next[index];
    const other = next[target];
    if (!moved || !other) return;
    next[index] = other;
    next[target] = moved;
    await act(() =>
      api.post(`/api/console/library/${section.id}/order`, {
        questionIds: next.map((question) => question.id),
      }),
    );
  };

  const patch = (body: Record<string, unknown>) =>
    act(() => api.patch(`/api/console/library/${section.id}`, body));

  if (!full) {
    return error ? (
      <div className="mt-5">
        <Notice tone="danger">{error}</Notice>
      </div>
    ) : (
      <Spinner label="Reading the section" />
    );
  }

  return (
    <div className="mt-5 space-y-5 border-t border-veil/[0.06] pt-5">
      {error && !field && <Notice tone="danger">{error}</Notice>}

      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Title">
          <Input
            defaultValue={full.title}
            onBlur={(event) => void patch({ title: event.target.value })}
          />
        </Field>
        <Field label="Kind" hint="Must be one a tenant composition accepts.">
          <Select
            defaultValue={full.kind}
            onChange={(event) => void patch({ kind: event.target.value })}
          >
            {KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kind}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Duration (minutes)" hint="Changing this bumps the version.">
          <Input
            type="number"
            min={1}
            defaultValue={Math.round(full.durationMs / 60000)}
            onBlur={(event) => void patch({ durationMs: Number(event.target.value) * 60000 })}
          />
        </Field>
      </div>

      <Field label="Summary" hint="One line, shown in the catalogue.">
        <Input
          defaultValue={full.summary ?? ''}
          onBlur={(event) => void patch({ summary: event.target.value })}
        />
      </Field>

      <Field label="Instructions" hint="Shown to the candidate above the section.">
        <Textarea
          rows={3}
          defaultValue={full.instructions ?? ''}
          onBlur={(event) => void patch({ instructions: event.target.value })}
        />
      </Field>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-code text-[11px] uppercase tracking-[0.16em] text-bone/30">
            {questions.length} questions
          </h3>
          {editing === null && (
            <Button onClick={() => setEditing({ id: null, draft: blankQuestion() })}>
              Add a question
            </Button>
          )}
        </div>

        {questions.length === 0 && editing === null ? (
          <div className="mt-3">
            <Empty
              title="No questions yet."
              detail="A section with none cannot be published — it would import as an empty section somebody then has to notice."
            />
          </div>
        ) : (
          <ul className="mt-2 divide-y divide-veil/[0.06]">
            {questions.map((question, index) => (
              <li key={question.id} className="py-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="prompt-text text-[12.5px] leading-relaxed text-bone/70">
                      {question.prompt}
                    </p>
                    <p className="mt-1 font-code text-[11px] uppercase tracking-[0.16em] text-bone/25">
                      {question.type} · {question.points} point{question.points === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="quiet"
                      disabled={index === 0 || busy}
                      onClick={() => void move(index, -1)}
                      aria-label="Move up"
                    >
                      ↑
                    </Button>
                    <Button
                      variant="quiet"
                      disabled={index === questions.length - 1 || busy}
                      onClick={() => void move(index, 1)}
                      aria-label="Move down"
                    >
                      ↓
                    </Button>
                    <Button
                      onClick={() =>
                        setEditing(
                          editing?.id === question.id
                            ? null
                            : { id: question.id, draft: toDraft(question) },
                        )
                      }
                    >
                      {editing?.id === question.id ? 'Close' : 'Edit'}
                    </Button>
                    <ConfirmButton
                      confirmLabel="Delete it"
                      busy={busy}
                      onConfirm={() =>
                        void act(() => api.delete(`/api/console/library/questions/${question.id}`))
                      }
                    >
                      Delete
                    </ConfirmButton>
                  </div>
                </div>

                {editing?.id === question.id && (
                  <QuestionEditor
                    state={editing}
                    onChange={setEditing}
                    onCancel={() => setEditing(null)}
                    onSave={saveQuestion}
                    busy={busy}
                    error={error}
                    field={field}
                  />
                )}
              </li>
            ))}
          </ul>
        )}

        {editing !== null && editing.id === null && (
          <QuestionEditor
            state={editing}
            onChange={setEditing}
            onCancel={() => setEditing(null)}
            onSave={saveQuestion}
            busy={busy}
            error={error}
            field={field}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant={full.published ? 'secondary' : 'primary'}
          busy={busy}
          onClick={() =>
            void act(() =>
              api.post(`/api/console/library/${full.id}/published`, { value: !full.published }),
            )
          }
        >
          {full.published ? 'Unpublish' : 'Publish'}
        </Button>
        <ConfirmButton
          confirmLabel="Archive it"
          busy={busy}
          onConfirm={() => void act(() => api.delete(`/api/console/library/${full.id}`))}
        >
          Archive
        </ConfirmButton>
        <span className="font-code text-[11px] uppercase tracking-[0.16em] text-bone/25">
          v{full.version} · {full.slug}
        </span>
      </div>
    </div>
  );
}

export default function Library() {
  const [sections, setSections] = useState<Section[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setSections((await api.get<{ sections: Section[] }>('/api/console/library')).sections);
      setError(null);
    } catch (failure) {
      setError(describeError(failure));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  if (error) {
    return (
      <ConsoleLayout back={{ to: '/console', label: 'Estate' }}>
        <Notice tone="danger">{error}</Notice>
      </ConsoleLayout>
    );
  }
  if (sections === null) {
    return (
      <ConsoleLayout back={{ to: '/console', label: 'Estate' }}>
        <Spinner label="Loading the library" />
      </ConsoleLayout>
    );
  }

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<Section>('/api/console/library', {
        title,
        durationMs: 600000,
        kind: 'cognitive',
      });
      setTitle('');
      await load();
      setOpen(created.id);
    } catch (failure) {
      setError(describeError(failure));
    } finally {
      setBusy(false);
    }
  };

  const live = sections.filter((section) => !section.archived);

  return (
    <ConsoleLayout back={{ to: '/console', label: 'Estate' }}>
      <header className="mb-6">
        <h1 className="text-[clamp(1.5rem,1.2rem+1vw,2rem)]">Section library</h1>
        <p className="mt-1.5 max-w-prose text-[13px] leading-relaxed text-bone/45">
          Ready-made sections every workspace can copy into its own questionnaires. A copy —
          editing one here never changes a questionnaire somebody has already built, which is what
          makes a filed report reflect what was actually asked.
        </p>
      </header>

      <div className="space-y-8">
        <section className="panel p-6">
          <h2 className="mb-1 text-[15.5px] font-medium">New section</h2>
          <p className="mb-5 text-[13px] text-bone/45">
            It starts as a draft, invisible to workspaces until you publish it.
          </p>
          <form onSubmit={create} className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
            <Field label="Title">
              <Input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Numerical reasoning — core"
                required
              />
            </Field>
            <Button type="submit" variant="primary" busy={busy}>
              Create
            </Button>
          </form>
        </section>

        <section className="panel p-6">
          <h2 className="mb-1 text-[15.5px] font-medium">The catalogue</h2>
          <p className="mb-5 text-[13px] text-bone/45">
            {live.length} section{live.length === 1 ? '' : 's'}, of which{' '}
            {live.filter((section) => section.published).length} published.
          </p>

          {live.length === 0 ? (
            <Empty
              title="The library is empty."
              detail="Run `observer library seed` to install the shipped sections."
            />
          ) : (
            <ul className="divide-y divide-veil/[0.06]">
              {live.map((section) => (
                <li key={section.id} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-[13.5px]">{section.title}</p>
                      {section.summary && (
                        <p className="mt-1 max-w-prose text-[12.5px] leading-relaxed text-bone/45">
                          {section.summary}
                        </p>
                      )}
                      <p className="mt-1.5 font-code text-[11px] uppercase tracking-[0.16em] text-bone/25">
                        {section.kind} · {section.questionCount} questions ·{' '}
                        {minutes(section.durationMs)} · v{section.version}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Chip tone={section.published ? 'good' : 'muted'}>
                        {section.published ? 'published' : 'draft'}
                      </Chip>
                      <Button onClick={() => setOpen(open === section.id ? null : section.id)}>
                        {open === section.id ? 'Close' : 'Edit'}
                      </Button>
                    </div>
                  </div>

                  {open === section.id && (
                    <Detail section={section} onChanged={() => void load()} />
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </ConsoleLayout>
  );
}
